import {
  IBenchmark,
  IPortfolio,
  IPreAfterTaxFileRecord,
} from '@/interfaces/portfolioOptimization.interface'
import axiosInstance from '@/utils/interceptor'
import dayjs from 'dayjs'

/* === General === */

// Cache for portfolio data (last 6 months) to prevent duplicate API calls
let portfolioCache: IPortfolio[] | null = null
let portfolioPromise: Promise<IPortfolio[]> | null = null

// Cache for portfolio data (all dates) to prevent duplicate API calls
let allPortfolioCache: IPortfolio[] | null = null
let allPortfolioPromise: Promise<IPortfolio[]> | null = null

const fetchPortfolios = (params?: { beginDate: string; endDate: string }): Promise<IPortfolio[]> => {
  return axiosInstance.get('portfolio', { params }).then((response) => {
    const allPortfolios = response.data as IPortfolio[]
    // Filter out swap portfolios (keep only type != 'swap')
    return allPortfolios.filter((p) => !p.type || p.type.toLowerCase() !== 'swap')
  })
}

/**
 * Get list of portfolios restricted to the last 6 months, with caching to prevent duplicate calls
 * @returns Promise<IPortfolio[]> portfolios whose date falls in the last 6 months
 */
export const getPortfolios = async (): Promise<IPortfolio[]> => {
  if (portfolioCache) {
    return portfolioCache
  }

  if (portfolioPromise) {
    return portfolioPromise
  }

  const endDate = dayjs().format('YYYYMMDD')
  const beginDate = dayjs().subtract(6, 'month').format('YYYYMMDD')

  portfolioPromise = fetchPortfolios({ beginDate, endDate })
    .then((portfolios) => {
      portfolioCache = portfolios
      portfolioPromise = null
      return portfolioCache
    })
    .catch((error) => {
      portfolioPromise = null
      throw error
    })

  return portfolioPromise
}

/**
 * Get full list of portfolios (no date restriction), with caching to prevent duplicate calls
 * @returns Promise<IPortfolio[]> all portfolios
 */
export const getAllPortfolios = async (): Promise<IPortfolio[]> => {
  if (allPortfolioCache) {
    return allPortfolioCache
  }

  if (allPortfolioPromise) {
    return allPortfolioPromise
  }

  allPortfolioPromise = fetchPortfolios()
    .then((portfolios) => {
      allPortfolioCache = portfolios
      allPortfolioPromise = null
      return allPortfolioCache
    })
    .catch((error) => {
      allPortfolioPromise = null
      throw error
    })

  return allPortfolioPromise
}

/**
 * Clear portfolio caches (both 6-month and all-dates) - useful for refreshing data
 */
export const clearPortfolioCache = (): void => {
  portfolioCache = null
  portfolioPromise = null
  allPortfolioCache = null
  allPortfolioPromise = null
}

/* === Optimization Parameters === */

/**
 * Get Optimization Types
 * @returns Promise<string[]> list of optimization types
 */
export const getOptimizationTypes = async (): Promise<string[]> => {
  const response = await axiosInstance.get(
    `optimizationParameter/optimization-type`,
  )
  return response.data as string[]
}

/**
 * Get Objective Types
 * @returns Promise<string[]> list of objective types
 */
export const getObjectiveTypes = async (): Promise<string[]> => {
  const response = await axiosInstance.get(
    `optimizationParameter/objective-type`,
  )
  return response.data as string[]
}

/**
 * Get Objective Items
 * @returns Promise<string[]> list of objective items
 */
export const getObjectiveItems = async (): Promise<string[]> => {
  const response = await axiosInstance.get(
    `optimizationParameter/objective-item`,
  )
  return response.data as string[]
}

/**
 * Get Liability Tax
 * @returns Promise<string[]> list of liability tax
 */
export const getLiabilities = async (): Promise<string[]> => {
  const response = await axiosInstance.get(
    `optimizationParameter/liability-tax`,
  )
  return response.data as string[]
}

/**
 * Get Contribution Withdrawal
 * @returns Promise<string[]> list of contribution withdrawal
 */
export const getContributionWithdrawal = async (): Promise<string[]> => {
  const response = await axiosInstance.get(
    `optimizationParameter/contribution-withdrawal`,
  )
  return response.data as string[]
}

/* === Others === */
export const getBenchmarks = async (): Promise<IBenchmark[]> => {
  const response = await axiosInstance.get('benchmark')
  return response.data as IBenchmark[]
}

// export const getCurrencies = async (): Promise<Currency[]> => {
//  return CurrenciesMockData
// const response = await axiosInstance.get('currencies')
// return response.data as Currency[]
// }

// export const getCountries = async (): Promise<Country[]> => {
//  return CountriesMockData
// const response = await axiosInstance.get('countries')
//  return response.data as Country[]
// }

// export const getBenchmarkHierarchy = async (): Promise<TreeViewBaseItem[]> => {
//  return BenchmarkHierarchyMockData
// const response = await axiosInstance.get(`optimization/benchmark-hierarchy`)
// return response.data as TreeViewBaseItem[]
//}

// export const getCashFlowData = async (): Promise<ICashFlowFileRecord[]> => {
//   return cashFlowMockData
//   // const response = await axiosInstance.get(`optimization/cash-flow`)
//   // return response.data as ICashFlowFileRecord[]
// }

// export const getReinvestmentData = async (): Promise<IRateFileRecord[]> => {
//   return reinvestmentMockData
//   // const response = await axiosInstance.get(`optimization/rates`)
//   // return response.data as IRateFileRecord[]
// }

export const getPreAfterTaxRatesData = async (): Promise<
  IPreAfterTaxFileRecord[]
> => {
  const response = await axiosInstance.get('pre-after-tax-rates')
  return response.data as IPreAfterTaxFileRecord[]
}
